/* OrderActions — sipariş satırındaki tek "Yazdır" butonu + ⋯ dropdown.
 * Iter 110/Faz2: 4 yan yana butondan tek-buton + popover'a indirildi.
 * Iter 113.7: Dropdown React Portal ile <body>'ye render edildi — sticky aksiyon
 * kolonunun stacking context'ini asar.
 */
import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    Printer, Loader2, MoreVertical, Truck, FileCheck, CheckCircle2,
    Trash2, ExternalLink, FileText,
} from 'lucide-react';

export default function OrderActions({
    order,
    compact = false,           // mobile mode: bigger touch targets
    isPrinting,
    isBpostBusy,
    isPushing,
    bpostConfigured,
    onPrint,
    onBpostPrint,
    onBpostRemove,
    onPush,
    onPickSlip,                // tek-sipariş pick slip — Faz2: asimetri giderildi
}) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const popRef = useRef(null);

    // Iter 113.5: dropdown'i FIXED pozisyonda render — parent overflow-x-auto'da clip oluyordu
    const openMenu = () => {
        if (!triggerRef.current) return;
        const r = triggerRef.current.getBoundingClientRect();
        const menuWidth = 224; // w-56
        // Buton'un altinda, sagdan hizali — ama sayfanin sagina tasarsa sola al
        const leftPx = Math.max(8, Math.min(r.right - menuWidth, window.innerWidth - menuWidth - 8));
        setPos({ top: r.bottom + 4, left: leftPx });
        setOpen(true);
    };

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (popRef.current && !popRef.current.contains(e.target) &&
                triggerRef.current && !triggerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const onScrollOrResize = () => setOpen(false);
        document.addEventListener('mousedown', onDoc);
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            window.removeEventListener('scroll', onScrollOrResize, true);
            window.removeEventListener('resize', onScrollOrResize);
        };
    }, [open]);

    const hasLabel = !!(order.bol_shipping_label_id || order.tracking?.code || order.tracking?.latest_event);
    const hasBpost = !!order.bpost_label;
    const pushed = !!order.billit_pushed;

    const btnSize = compact ? 'h-9 px-3 text-xs' : 'h-7 px-2 text-[11px]';
    const iconSize = compact ? 13 : 11;

    const printColor = hasLabel ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-cyan-700 hover:bg-cyan-800';

    return (
        <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {/* Ana aksiyon — Yazdır (her zaman aynı metin) */}
            <button
                onClick={() => onPrint(order)}
                disabled={isPrinting}
                className={`${btnSize} font-bold rounded-md inline-flex items-center gap-1 whitespace-nowrap text-white disabled:opacity-50 shadow-sm transition-transform active:scale-95 ${printColor}`}
                title={hasLabel
                    ? `Etiket mevcut — yeniden bas${hasBpost ? ' (Bol + bpost + pack slip = 3 sayfa)' : ''}`
                    : 'Bol etiketi al + yazdır'}
                data-testid={`bol-print-${order.order_id}`}>
                {isPrinting ? <Loader2 size={iconSize} className="animate-spin" /> : <Printer size={iconSize} />}
                Yazdır
                {hasBpost && (
                    <span className="ml-0.5 px-1 rounded bg-white/25 text-[9px] font-extrabold">+bpost</span>
                )}
            </button>

            {/* Push (zaten basit, ayrı kalsın) */}
            {pushed ? (
                <span title="Billit'e aktarıldı" className="inline-flex items-center text-emerald-600">
                    <CheckCircle2 size={iconSize + 3} />
                </span>
            ) : (
                <button
                    onClick={() => onPush(order)}
                    disabled={isPushing}
                    className={`${compact ? 'h-9 w-9' : 'h-7 w-7'} flex items-center justify-center text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-md shadow-sm active:scale-95 transition-transform`}
                    title="Billit'e fatura aktar"
                    data-testid={`bol-push-${order.order_id}`}>
                    {isPushing ? <Loader2 size={iconSize} className="animate-spin" /> : <FileCheck size={iconSize} />}
                </button>
            )}

            {/* ⋯ Dropdown — ikincil aksiyonlar (FIXED pozisyon — table overflow clip etmesin diye) */}
            <div className="relative">
                <button
                    ref={triggerRef}
                    onClick={() => (open ? setOpen(false) : openMenu())}
                    className={`${compact ? 'h-9 w-7' : 'h-7 w-6'} flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-md`}
                    title="Daha fazla"
                    data-testid={`bol-more-${order.order_id}`}>
                    <MoreVertical size={iconSize + 1} />
                </button>
                {open && createPortal(
                    <div
                        ref={popRef}
                        style={{ position: 'fixed', top: pos.top, left: pos.left, width: 224, zIndex: 9999 }}
                        className="bg-white border border-gray-200 rounded-lg shadow-xl py-1"
                        data-testid={`bol-actions-menu-${order.order_id}`}>
                        {bpostConfigured && (
                            <MenuItem
                                icon={isBpostBusy ? <Loader2 size={14} className="animate-spin" /> : <Truck size={14} />}
                                label={hasBpost ? 'bpost etiketi yeniden bas' : 'bpost ile destek etiketi bas'}
                                hint={hasBpost ? `Track: ${order.bpost_label.track || '-'}` : 'Bol\'a dokunmaz'}
                                onClick={() => { setOpen(false); onBpostPrint(order); }}
                                disabled={isBpostBusy}
                                testid={`bpost-print-${order.order_id}`}
                                tone="rose"
                            />
                        )}
                        {hasBpost && (
                            <MenuItem
                                icon={<Trash2 size={14} />}
                                label="bpost etiketini sil"
                                hint="Flowbil cache'inden kaldır"
                                onClick={() => { setOpen(false); onBpostRemove(order); }}
                                testid={`bpost-remove-${order.order_id}`}
                                tone="rose"
                            />
                        )}
                        {onPickSlip && (
                            <MenuItem
                                icon={<FileText size={14} />}
                                label="Sadece pack slip"
                                hint="Bol API kullanmaz"
                                onClick={() => { setOpen(false); onPickSlip(order); }}
                                testid={`bol-pickslip-${order.order_id}`}
                            />
                        )}
                        <a
                            href={`https://retailer.bol.com/order/${order.order_id}`}
                            target="_blank" rel="noreferrer"
                            onClick={() => setOpen(false)}
                            className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer"
                            data-testid={`bol-open-${order.order_id}`}>
                            <ExternalLink size={14} />
                            <span>Bol panelinde aç</span>
                        </a>
                    </div>,
                    document.body
                )}
            </div>
        </div>
    );
}

function MenuItem({ icon, label, hint, onClick, disabled, testid, tone }) {
    const toneCls = tone === 'rose'
        ? 'text-rose-700 hover:bg-rose-50'
        : 'text-gray-700 hover:bg-gray-50';
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            className={`w-full text-left flex items-start gap-2 px-3 py-2 disabled:opacity-50 ${toneCls}`}>
            <span className="mt-0.5">{icon}</span>
            <span className="flex-1">
                <div className="text-xs font-semibold">{label}</div>
                {hint && <div className="text-[10px] text-gray-500">{hint}</div>}
            </span>
        </button>
    );
}
